import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

import Loader from 'components/partials/loader/loader';

import { usePowerBiTokenMutation } from 'hooks/use-power-bi-token-mutation';

import { getOktaUserId } from 'helpers/okta-session';

import powerbi, { pbiModels } from 'utils/powerbi';
import { readActivePageAndSlicerFilters } from 'utils/powerbi-metadata/powerbi-metadata';

interface PowerBiMetadata {
  activePage: unknown;
  filters: unknown;
}

interface PowerBiPage {
  isActive?: boolean;
  defaultSize?: {
    width?: number;
    height?: number;
  };
}

interface PowerBiBookmark {
  state: string;
}

interface PowerBiReportInstance {
  getActivePage(): Promise<PowerBiPage & { getVisuals: () => Promise<unknown[]> }>;
  getPages(): Promise<PowerBiPage[]>;
  bookmarksManager: {
    applyState(state: string): Promise<void>;
    capture(options: { allPages: boolean }): Promise<PowerBiBookmark>;
  };
  on(eventName: string, handler: (event?: unknown) => void): void;
}

export interface PowerBiReportHandle {
  captureBookmark(): Promise<string | null>;
  readonly reportInstance: unknown;
}

interface PowerBiReportProps {
  reportId: string;
  clientId?: string | null;
  constrainToViewport?: boolean;
  fitToWidth?: boolean;
  onError?: (error: unknown) => void;
  onReportLoaded?: () => void;
  onProcessingChange?: (processing: boolean) => void;
  customSettings?: Record<string, unknown>;
  relatedPageId?: string;
  customClass?: string;
  onMetadata?: (metadata: PowerBiMetadata) => void;
  containerId?: string;
  bookmarkState?: string;
}

const initPowerBi = (reportRef: React.RefObject<HTMLDivElement | null>) => {
  if (!reportRef.current) return;

  powerbi.bootstrap(reportRef.current, { type: 'report' });
};

const PowerBiReport = forwardRef<PowerBiReportHandle, PowerBiReportProps>(
  (
    {
      reportId,
      clientId = null,
      constrainToViewport = true,
      fitToWidth = false,
      onError,
      onReportLoaded,
      onProcessingChange: _onProcessingChange,
      customSettings,
      relatedPageId,
      customClass = '',
      onMetadata,
      containerId = 'powerbi-report-embed',
      bookmarkState,
    },
    ref
  ) => {
    const reportRef = useRef<HTMLDivElement>(null);
    const reportInstanceRef = useRef<PowerBiReportInstance | null>(null);
    const hasCalledLoadedCallbackRef = useRef(false);

    const tokenMutation = usePowerBiTokenMutation();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

    const emitMetadata = async (reportInstance: PowerBiReportInstance) => {
      if (!reportInstance || !onMetadata) return;

      try {
        const { activePage, filters: normalized } =
          await readActivePageAndSlicerFilters(reportInstance);

        onMetadata({ activePage, filters: normalized });
      } catch (error) {
        console.warn('Failed to get PowerBI metadata', error);
      }
    };

    const setupIframeAccessibility = () => {
      const iframe = reportRef.current?.querySelector('iframe');

      if (!iframe) return;

      if (!iframe.getAttribute('sandbox')) {
        iframe.setAttribute('sandbox', '');
      }

      if (!iframe.title) {
        iframe.setAttribute('title', 'MARKETview Report');
      }
    };

    const updateDimensionsFromReport = async (report: PowerBiReportInstance) => {
      try {
        const pages = await report.getPages();
        const activePage = pages.find((p) => p.isActive);

        if (activePage?.defaultSize) {
          const { width, height } = activePage.defaultSize;

          if (width && height) {
            setDimensions({ width, height });
          }
        }
      } catch (e) {
        console.warn('Failed to get report dimensions', e);
      }
    };

    const handleReportLoaded = async (report: PowerBiReportInstance) => {
      if (bookmarkState) {
        try {
          await report.bookmarksManager.applyState(bookmarkState);
        } catch (error) {
          console.error('Failed to apply bookmark state', error);
        }
      } else {
        await emitMetadata(report);
      }

      await updateDimensionsFromReport(report);
    };

    const handleReportRendered = async (report: PowerBiReportInstance) => {
      await emitMetadata(report);

      if (!hasCalledLoadedCallbackRef.current && onReportLoaded) {
        hasCalledLoadedCallbackRef.current = true;
        onReportLoaded();
      }
    };

    const handleReportError = (errorObject?: unknown) => {
      const err = (errorObject as { detail?: { message?: string; detailedMessage?: string } } | undefined)?.detail;

      console.error(
        `PowerBI Error: ${err?.message ?? 'Unknown error'}. Details: ${err?.detailedMessage ?? ''}`,
        err
      );
    };

    const handlePageChanged = async (report: PowerBiReportInstance) => {
      await emitMetadata(report);
      await updateDimensionsFromReport(report);
    };

    const setupReportEventListeners = (report: PowerBiReportInstance) => {
      report.on('rendered', setupIframeAccessibility);
      report.on('loaded', () => handleReportLoaded(report));
      report.on('rendered', () => handleReportRendered(report));
      report.on('pageChanged', () => handlePageChanged(report));
      report.on('error', handleReportError);
    };

    const createEmbedConfiguration = (accessToken: string) => ({
      type: 'report',
      id: reportId,
      embedUrl: 'https://app.powerbi.com/reportEmbed',
      tokenType: pbiModels.TokenType.Embed,
      accessToken,
      viewMode: pbiModels.ViewMode.View,
      permissions: pbiModels.Permissions.Read,
      pageName: relatedPageId,
      settings: {
        background: pbiModels.BackgroundType.Transparent,
        navContentPaneEnabled: false,
        filterPaneEnabled: false,
        visualSettings: {
          visualHeaders: [{ settings: {} }],
        },
        ...(fitToWidth && {
          layoutType: pbiModels.LayoutType.Custom,
          customLayout: {
            displayOption: pbiModels.DisplayOption.FitToWidth,
          },
        }),
        ...(customSettings || {}),
      },
    });

    const loadReport = () => {
      setLoading(true);
      hasCalledLoadedCallbackRef.current = false;

      const userId = getOktaUserId();

      if (!userId) {
        setLoading(false);
        const err = new Error('Missing Okta user id for Power BI token');

        setError(err);
        onError?.(err);

        return;
      }

      tokenMutation
        .mutateAsync({
          userId,
          powerbiReportId: reportId,
          data:
            clientId != null && clientId !== ''
              ? { clientId: Number(clientId) }
              : undefined,
        })
        .then((dto) => {
          setLoading(false);

          const token = dto.token;

          if (!token) {
            const err = new Error('Power BI token missing from response');

            setError(err);
            onError?.(err);

            return;
          }

          const embedConfiguration = createEmbedConfiguration(token);

          try {
            if (!reportRef.current) return;

            const report = powerbi.embed(
              reportRef.current,
              embedConfiguration,
            ) as unknown as PowerBiReportInstance;

            reportInstanceRef.current = report;
            setupReportEventListeners(report);
          } catch (error) {
            console.error('Error embedding PowerBI report', error);
          }
        })
        .catch((error) => {
          setLoading(false);
          setError(error);
          onError?.(error);
        });
    };

    useImperativeHandle(
      ref,
      () => ({
        async captureBookmark() {
          if (!reportInstanceRef.current) return null;

          const bookmark =
            await reportInstanceRef.current.bookmarksManager.capture({
              allPages: true,
            });

          return bookmark.state;
        },
        get reportInstance() {
          return reportInstanceRef.current;
        },
      }),
      []
    );

    useEffect(() => {
      initPowerBi(reportRef);
    }, []);

    useEffect(() => {
      loadReport();
    }, [reportId, clientId]);

    if (error) return null;

    const containerClasses = classNames('border-none', {
      invisible: loading,
    });

    const reportStyles: React.CSSProperties = dimensions
      ? {
          width: '100%',
          maxWidth: `${dimensions.width}px`,
          height: 'auto',
          aspectRatio: `${dimensions.width} / ${dimensions.height}`,
          minWidth: `${dimensions.width / 2}px`,
          minHeight: '580px',
          ...(constrainToViewport && { maxHeight: 'calc(100vh - 200px)' }),
        }
      : { minHeight: '750px', width: '100%' };

    return (
      <>
        <Loader loading={loading} fullScreen={false} />
        <div className={containerClasses} id={containerId}>
          <div
            ref={reportRef}
            className={classNames('border-none', customClass)}
            style={reportStyles}
          />
        </div>
      </>
    );
  }
);

PowerBiReport.displayName = 'PowerBiReport';

export default PowerBiReport;
